import type { FC, ReactNode } from 'react'
import type { PersonSummary } from '@/types'

import Link from 'next/link'
import { ArrowRight, UserPlus } from 'lucide-react'
import { buttonClass } from '@/components/generals/button'
import { Reveal } from '@/components/generals/reveal'
import { SectionHeading } from '@/components/generals/section-heading'

import PersonCard from '@/components/family/person-card'

interface FamilySectionProps {
    relatives: Array<PersonSummary>
    peopleCount: number
}

const FamilySection: FC<FamilySectionProps> = ({ relatives, peopleCount }): ReactNode => {
    return (
        <section id='meet-the-family' className='px-5 py-20 md:px-8 md:py-24'>
            <div className='mx-auto max-w-6xl'>
                <div className='flex flex-col gap-6 md:flex-row md:items-end md:justify-between'>
                    <SectionHeading
                        eyebrow="Meet the family"
                        title="The names that make us who we are"
                        text="Parents and grandparents, partners and children, cousins near and far. Open any name to read their story and see how they fit into the tree."
                    />

                    {relatives.length > 0 && (
                        <Link href='/tree' className={buttonClass({ variant: 'quiet', className: 'group shrink-0 text-base' })}>
                            See all {peopleCount} relatives
                            <ArrowRight className='size-4 transition-transform duration-200 group-hover:translate-x-1' aria-hidden='true' />
                        </Link>
                    )}
                </div>

                {relatives.length > 0 ? (
                    <ul className='mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3'>
                        {relatives.map((person, index) => (
                            <Reveal as='li' key={person.id} delay={(index % 3) * 80}>
                                <PersonCard person={person} className='h-full' />
                            </Reveal>
                        ))}
                    </ul>
                ) : (
                    <Reveal className='mt-12 flex flex-col items-start gap-6 rounded-[2rem] border border-dashed border-gold/70 bg-linen/60 p-8 md:flex-row md:items-center md:justify-between md:p-10'>
                        <div className='flex items-start gap-5'>
                            <span className='grid size-12 shrink-0 place-items-center rounded-full bg-clay-soft text-clay'>
                                <UserPlus className='size-5' aria-hidden='true' />
                            </span>

                            <div>
                                <h3 className='font-heading text-2xl font-semibold text-ink'>
                                    The branches are still growing
                                </h3>
                                <p className='mt-2 max-w-xl text-base leading-relaxed text-umber'>
                                    Know a parent, a partner, a child or a cousin who belongs here? Send us their name and anything you know about them, and they will be added to the tree.
                                </p>
                            </div>
                        </div>

                        <Link href='/share' className={buttonClass({ className: 'shrink-0' })}>
                            Suggest a relative
                        </Link>
                    </Reveal>
                )}
            </div>
        </section>
    )
}

export default FamilySection
